import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BLOG_URL, CAREERS_URL } from '@/constants/links';

type NavLink =
  | { label: string; path: string }
  | { label: string; href: string; external: true };

const navLinks: NavLink[] = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'About', path: '/about' },
  { label: 'Technology', path: '/technology' },
  { label: 'Facilities', path: '/facilities' },
  { label: 'Industries', path: '/industries' },
  { label: 'Careers', href: CAREERS_URL, external: true },
  { label: 'Fulfilment intelligence', href: BLOG_URL, external: true },
];

const handleNavClick = (
  link: NavLink,
  navigate: ReturnType<typeof useNavigate>,
) => {
  if ('external' in link) {
    window.open(link.href, '_blank', 'noopener,noreferrer');
    return;
  }
  navigate(link.path);
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === '/';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || !isHome
            ? 'bg-thl-dark border-b border-thl-border'
            : 'bg-transparent'
        }`}
        style={{ height: 70 }}
      >
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center cursor-pointer"
          >
            <img
              src="https://public.readdy.ai/ai/img_res/edited_2327a554216bf9e95ffa821d05c0e0af_e62d8e34.jpg"
              alt="The Hub Terminus Limited"
              className="h-10 w-auto object-contain"
              style={{
                mixBlendMode: 'screen',
                filter: 'brightness(1.15)',
              }}
            />
          </button>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map(link => {
              const linkKey = 'external' in link ? link.href : link.path;
              const isActive = 'path' in link && location.pathname === link.path;

              return (
                <button
                  key={linkKey}
                  onClick={() => handleNavClick(link, navigate)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    isActive
                      ? 'text-white bg-white/10'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </button>
              );
            })}
          </div>

          {/* Right actions */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={() => navigate('/contact')}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors cursor-pointer whitespace-nowrap px-3 py-2"
            >
              Contact
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="btn-primary text-sm"
            >
              Get a Quote →
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(v => !v)}
            className="lg:hidden flex flex-col gap-1.5 p-2 cursor-pointer"
          >
            <span className={`block w-5 h-0.5 bg-white transition-all duration-200 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-5 h-0.5 bg-white transition-all duration-200 ${mobileOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-5 h-0.5 bg-white transition-all duration-200 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`fixed top-[70px] left-0 right-0 z-40 bg-thl-dark border-b border-thl-border transition-all duration-300 ${
          mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          {navLinks.map(link => (
            <button
              key={'external' in link ? link.href : link.path}
              onClick={() => handleNavClick(link, navigate)}
              className="text-left px-4 py-3 text-sm font-medium text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-all cursor-pointer"
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => navigate('/contact')}
            className="mt-2 btn-primary text-sm text-center"
          >
            Get a Quote →
          </button>
        </div>
      </div>
    </>
  );
}
